// Delivery-method + buy-button helpers for the PDP, matching the Figma design.
//
// 1. Relabel Supercycle's "Ship" / "Pick up" delivery options to "Deliver" /
//    "NYC Pickup". Supercycle (React) re-renders on variant/date changes, so we
//    re-apply on every mutation.
// 2. Relabel the buy button to "Rent now" and move Supercycle's rental total
//    into it (right-aligned), suppressed in non-purchasable states.
// 3. Show a "pick up at our NYC Showroom" note when NYC Pickup is selected.
// 4. When Damage Protection is already in the cart, show Supercycle's native
//    add-on tile as selected and disabled so it can't be added twice.
// 5. Add a "what's covered" tooltip to the Damage Protection add-on title
//    (hover the title or the "?", tap the "?" on touch).
(function () {
  var LABELS = { 'Ship': 'Deliver', 'Pick up': 'NYC Pickup' };

  var DP_HANDLE = 'damage-protection';
  var DP_TITLE = 'Damage Protection';
  var dpInCart = false;

  var PICKUP_NOTE_HTML =
    '<div class="supercycle-pickup-note">' +
      '<span class="supercycle-pickup-note__dot"></span>' +
      '<span class="supercycle-pickup-note__text">Pick up at our showroom in Chelsea.<br>Hours: Mon-Fri, 10am - 6pm; Sat, 11am - 5pm. Closed Sundays.<br>No-show pickups are non-refundable.</span>' +
      '<span class="supercycle-pickup-note__help">' +
        '<button type="button" class="supercycle-pickup-note__help-btn" aria-expanded="false" aria-describedby="supercycle-pickup-tooltip" aria-label="Pickup address info">?</button>' +
        '<span class="supercycle-pickup-note__tooltip" id="supercycle-pickup-tooltip" role="tooltip">Address will be shared after checkout</span>' +
      '</span>' +
    '</div>';

  // Injected into the Damage Protection add-on title (see syncDpTiles).
  var DP_TOOLTIP_HTML =
    '<span class="iom-dp-help">' +
      '<button type="button" class="iom-dp-help__btn" aria-expanded="false" aria-describedby="iom-dp-tooltip" aria-label="What Damage Protection covers">?</button>' +
      '<span class="iom-dp-help__tooltip" id="iom-dp-tooltip" role="tooltip">' +
        'Damage protection covers damage beyond normal wear and tear. This includes accidental damage such as rips larger than 1/4 inch, embellishment breakage, red wine spills, and more (see our ' +
        '<a href="/policies/terms-of-service" target="_blank" rel="noopener">terms and conditions</a> for full list). ' +
        'Damage protection does not account for complete destruction of the item (even if accidental) and it does not cover you for a lost item or intentional damage through pinning, clipping, altering, hemming, or otherwise modifying the garment.' +
      '</span>' +
    '</span>';

  // Tooltips open on hover/focus via CSS; this handles tap-to-toggle on touch
  // devices, closing on outside tap or Escape. Delegated so it survives the
  // note being re-injected.
  var OPEN_CLASSES = ['supercycle-pickup-note__help--open', 'iom-dp-help--open'];
  function closeHelp() {
    OPEN_CLASSES.forEach(function (cls) {
      document.querySelectorAll('.' + cls).forEach(function (open) {
        open.classList.remove(cls);
        var btn = open.querySelector('button');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    });
  }
  function toggleHelp(btn, openClass) {
    var help = btn.parentElement;
    var isOpen = !help.classList.contains(openClass);
    closeHelp();
    help.classList.toggle(openClass, isOpen);
    btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.supercycle-pickup-note__help-btn');
    if (!btn) { closeHelp(); return; }
    toggleHelp(btn, 'supercycle-pickup-note__help--open');
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeHelp();
  });

  function relabel() {
    document.querySelectorAll('.supercycle-options__option-title').forEach(function (el) {
      var text = el.textContent.trim();
      // Only swap known source labels; once swapped the new text isn't a key,
      // so this is idempotent and won't loop with the observer below.
      if (LABELS[text]) el.textContent = LABELS[text];
    });
  }

  // Render the NYC Showroom note whenever the delivery-method toggle exists, and
  // toggle its visibility based on whether NYC Pickup is selected. Keeping it in
  // the DOM (just hidden) reserves its height so switching methods doesn't shift
  // the layout. Injected as a sibling AFTER <x-methods> (outside Supercycle's
  // React root) so React never reconciles it — same rule that fixed the crash.
  function syncPickupNote() {
    var xm = document.querySelector('x-methods');
    if (!xm) return;
    var toggle = xm.querySelector('.supercycle-options');
    var note = document.querySelector('.supercycle-pickup-note');
    if (!toggle) {
      // No delivery-method toggle (single method) → no note.
      if (note) note.remove();
      return;
    }
    if (!note) {
      xm.insertAdjacentHTML('afterend', PICKUP_NOTE_HTML);
      note = document.querySelector('.supercycle-pickup-note');
    }
    var selected = xm.querySelector('.supercycle-options__option--selected .supercycle-options__option-title');
    var title = selected ? selected.textContent.trim() : '';
    var isPickup = title === 'NYC Pickup' || title === 'Pick up';
    note.classList.toggle('supercycle-pickup-note--active', isPickup);
  }

  // Relabel the buy button to "Rent now" and move Supercycle's "Total $387.00"
  // into it (right-aligned). The summary itself is hidden via CSS. Both the label
  // and the price are suppressed in non-purchasable states (sold out / notify),
  // and re-applied on every mutation since the theme resets the label on variant
  // changes.
  function syncButton() {
    // Only the real add-to-cart submit; skips the waitlist "Notify Me" swap button.
    var btn = document.querySelector('.product-form__submit[name="add"]');
    if (!btn) return;
    var labelEl = btn.querySelector('span');
    var addToCart = (window.variantStrings && window.variantStrings.addToCart) || 'Add to Bag';
    var soldOut = (window.variantStrings && window.variantStrings.soldOut) || 'Sold Out';
    var notifyBtn = document.querySelector('.notify-button');
    var unavailable =
      (notifyBtn && notifyBtn.hasAttribute('disabled')) ||
      (labelEl && labelEl.textContent.trim() === soldOut);

    // Relabel "Add to Bag" → "Rent now" (purchasable states only).
    if (!unavailable && labelEl && labelEl.textContent.trim() === addToCart) {
      labelEl.textContent = 'Rent now';
    }

    var totalEl = document.querySelector('.supercycle-summary-line--total .supercycle-summary-line__value');
    var price = (!unavailable && totalEl) ? totalEl.textContent.trim() : '';
    var priceEl = btn.querySelector('.product-form__submit-total');
    if (price) {
      if (!priceEl) {
        priceEl = document.createElement('span');
        priceEl.className = 'product-form__submit-total';
        btn.appendChild(priceEl);
      }
      if (priceEl.textContent !== price) priceEl.textContent = price;
    } else if (priceEl) {
      priceEl.remove();
    }
  }

  // Supercycle's add-on tile is React-rendered with no data attributes, so the
  // Damage Protection tile is identified by its title text. Marking relies on
  // the app's own `--selected` modifier for the checked look; the injected CSS
  // handles the disabled look and blocks clicks. React re-renders strip the
  // classes, but the mutation observer re-applies them.
  //
  // The coverage tooltip is appended inside the title span (React only ever
  // updates its own text node there, so an extra trailing child is safe). The
  // whole tile is Supercycle's onClick toggle, so clicks inside the help must
  // be stopped at the element itself, before they bubble to React's root.
  function dpTitleText(title) {
    var text = '';
    title.childNodes.forEach(function (n) { if (n.nodeType === 3) text += n.nodeValue; });
    return text.trim();
  }
  function injectDpTooltip(title) {
    if (title.querySelector('.iom-dp-help')) return;
    title.insertAdjacentHTML('beforeend', DP_TOOLTIP_HTML);
    var help = title.querySelector('.iom-dp-help');
    help.addEventListener('click', function (e) {
      e.stopPropagation();
      if (e.target.closest('a')) return; // let the T&C link navigate
      var btn = e.target.closest('.iom-dp-help__btn');
      if (btn) toggleHelp(btn, 'iom-dp-help--open');
    });
  }
  function syncDpTiles() {
    document.querySelectorAll('.supercycle-add-on').forEach(function (tile) {
      var title = tile.querySelector('.supercycle-add-on__title');
      if (!title || dpTitleText(title) !== DP_TITLE) return;
      injectDpTooltip(title);
      var price = tile.querySelector('.supercycle-add-on__price');
      if (dpInCart) {
        tile.classList.add('supercycle-add-on--selected', 'supercycle-add-on--iom-in-cart');
        tile.setAttribute('aria-disabled', 'true');
        if (price && price.textContent !== 'Added') {
          price.dataset.iomPrice = price.textContent;
          price.textContent = 'Added';
        }
      } else if (tile.classList.contains('supercycle-add-on--iom-in-cart')) {
        // Only unwind state this script added, in case the shopper has the
        // tile genuinely selected in Supercycle's own state.
        tile.classList.remove('supercycle-add-on--selected', 'supercycle-add-on--iom-in-cart');
        tile.removeAttribute('aria-disabled');
        if (price && price.dataset.iomPrice) {
          price.textContent = price.dataset.iomPrice;
          delete price.dataset.iomPrice;
        }
      }
    });
  }

  // Dims the tile's own parts rather than the tile, so the coverage tooltip
  // (a descendant) stays fully legible and reachable when the add-on is in cart.
  var dpStyle = document.createElement('style');
  dpStyle.textContent =
    '.supercycle-add-on--iom-in-cart { pointer-events: none; }' +
    '.supercycle-add-on--iom-in-cart .iom-dp-help { pointer-events: auto; }' +
    '.supercycle-add-on--iom-in-cart .supercycle-add-on__check,' +
    '.supercycle-add-on--iom-in-cart .supercycle-add-on__price { opacity: .6; }' +
    '.supercycle-add-on--iom-in-cart .supercycle-add-on__title { color: color-mix(in srgb, currentColor 60%, transparent); }' +
    '.supercycle-add-on--iom-in-cart .supercycle-add-on__check svg { visibility: visible !important; opacity: 1 !important; }';
  document.head.appendChild(dpStyle);

  function refreshDpState() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var inCart = (cart.items || []).some(function (item) { return item.handle === DP_HANDLE; });
        if (inCart !== dpInCart) {
          dpInCart = inCart;
          syncDpTiles();
        }
      })
      .catch(function () {});
  }

  // Re-check after any cart mutation. Supercycle adds the add-on through its
  // own fetch calls, so patching fetch catches both theme and app requests.
  var origFetch = window.fetch;
  window.fetch = function () {
    var arg = arguments[0];
    var url = typeof arg === 'string' ? arg : (arg && arg.url) || '';
    var result = origFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)/.test(url)) {
      result.then(refreshDpState, function () {});
    }
    return result;
  };

  function apply() {
    relabel();
    syncPickupNote();
    syncButton();
    syncDpTiles();
  }

  refreshDpState();
  apply();
  new MutationObserver(apply).observe(document.body, { childList: true, subtree: true });
})();
